/**
 * Application State Management
 *
 * Central state management for the puzzle builder application.
 * Migrates the 27 global variables (g_*) to a typed state object.
 */

import type { AppStateData } from '../types/state.types';
import { DEFAULT_APP_STATE } from '../types/state.types';

/**
 * Create a deep copy of the default state to avoid shared references.
 */
function createDefaultState(): AppStateData {
    return {
        puzzleId: DEFAULT_APP_STATE.puzzleId,
        solutionId: DEFAULT_APP_STATE.solutionId,
        puzzle: null,
        settings: null,
        history: {
            packages: [],
            currentPackage: null,
            sequenceNumber: DEFAULT_APP_STATE.history.sequenceNumber,
            maxPackages: DEFAULT_APP_STATE.history.maxPackages,
        },
        mainMenu: null,
        solutionMenu: null,
        clueMenu: null,
        selection: {
            currentElement: null,
            currentCellId: DEFAULT_APP_STATE.selection.currentCellId,
            currentSelection: [],
            inverseSelection: [],
            isHorizontal: DEFAULT_APP_STATE.selection.isHorizontal,
        },
        mode: {
            isBuilderMode: DEFAULT_APP_STATE.mode.isBuilderMode,
            isSaved: DEFAULT_APP_STATE.mode.isSaved,
            isVerified: DEFAULT_APP_STATE.mode.isVerified,
        },
        ui: {
            cellHighlight: DEFAULT_APP_STATE.ui.cellHighlight,
            clueMarking: DEFAULT_APP_STATE.ui.clueMarking,
            syncEditing: DEFAULT_APP_STATE.ui.syncEditing,
            knownMarking: DEFAULT_APP_STATE.ui.knownMarking,
            maskMarking: DEFAULT_APP_STATE.ui.maskMarking,
            allowImages: DEFAULT_APP_STATE.ui.allowImages,
            hasGraphics: DEFAULT_APP_STATE.ui.hasGraphics,
            allowColored: DEFAULT_APP_STATE.ui.allowColored,
            allowBridge: DEFAULT_APP_STATE.ui.allowBridge,
            allowEmpty: DEFAULT_APP_STATE.ui.allowEmpty,
            allowBlack: DEFAULT_APP_STATE.ui.allowBlack,
            allowSolution: DEFAULT_APP_STATE.ui.allowSolution,
            hasMultipleGrids: DEFAULT_APP_STATE.ui.hasMultipleGrids,
        },
        assets: {
            imagePath: DEFAULT_APP_STATE.assets.imagePath,
            borderColor: DEFAULT_APP_STATE.assets.borderColor,
            coloredBase64: DEFAULT_APP_STATE.assets.coloredBase64,
            bridgeBase64: DEFAULT_APP_STATE.assets.bridgeBase64,
        },
        validation: {
            validCharPattern: DEFAULT_APP_STATE.validation.validCharPattern,
        },
    };
}

/**
 * Application State Manager
 *
 * Provides centralized state management with type safety.
 * Supports exposing legacy global variables for backward compatibility.
 */
export class AppState {
    private _state: AppStateData;

    constructor(initialState?: Partial<AppStateData>) {
        this._state = createDefaultState();
        if (initialState) {
            Object.assign(this._state, initialState);
        }
    }

    // ============================================================================
    // Generic Getters/Setters
    // ============================================================================

    /**
     * Get a state value by key.
     */
    get<K extends keyof AppStateData>(key: K): AppStateData[K] {
        return this._state[key];
    }

    /**
     * Set a state value by key.
     */
    set<K extends keyof AppStateData>(key: K, value: AppStateData[K]): void {
        this._state[key] = value;
    }

    /**
     * Get the entire state (read-only).
     */
    getState(): Readonly<AppStateData> {
        return this._state;
    }

    /**
     * Reset state to defaults.
     */
    reset(): void {
        this._state = createDefaultState();
    }

    // ============================================================================
    // Core State - Puzzle
    // ============================================================================

    get puzzleId(): number {
        return this._state.puzzleId;
    }

    set puzzleId(value: number) {
        this._state.puzzleId = value;
    }

    get solutionId(): number {
        return this._state.solutionId;
    }

    set solutionId(value: number) {
        this._state.solutionId = value;
    }

    // ============================================================================
    // Selection State
    // ============================================================================

    get currentElement(): HTMLElement | null {
        return this._state.selection.currentElement;
    }

    set currentElement(value: HTMLElement | null) {
        this._state.selection.currentElement = value;
    }

    get currentCellId(): number {
        return this._state.selection.currentCellId;
    }

    set currentCellId(value: number) {
        this._state.selection.currentCellId = value;
    }

    get currentSelection(): number[] {
        return this._state.selection.currentSelection;
    }

    set currentSelection(value: number[]) {
        this._state.selection.currentSelection = value;
    }

    get inverseSelection(): number[] {
        return this._state.selection.inverseSelection;
    }

    set inverseSelection(value: number[]) {
        this._state.selection.inverseSelection = value;
    }

    get isHorizontal(): boolean {
        return this._state.selection.isHorizontal;
    }

    set isHorizontal(value: boolean) {
        this._state.selection.isHorizontal = value;
    }

    // ============================================================================
    // Mode State
    // ============================================================================

    get isBuilderMode(): boolean {
        return this._state.mode.isBuilderMode;
    }

    set isBuilderMode(value: boolean) {
        this._state.mode.isBuilderMode = value;
    }

    get isSaved(): boolean {
        return this._state.mode.isSaved;
    }

    set isSaved(value: boolean) {
        this._state.mode.isSaved = value;
    }

    get isVerified(): boolean {
        return this._state.mode.isVerified;
    }

    set isVerified(value: boolean) {
        this._state.mode.isVerified = value;
    }

    // ============================================================================
    // UI Settings State
    // ============================================================================

    get cellHighlight(): boolean {
        return this._state.ui.cellHighlight;
    }

    set cellHighlight(value: boolean) {
        this._state.ui.cellHighlight = value;
    }

    get clueMarking(): boolean {
        return this._state.ui.clueMarking;
    }

    set clueMarking(value: boolean) {
        this._state.ui.clueMarking = value;
    }

    get syncEditing(): boolean {
        return this._state.ui.syncEditing;
    }

    set syncEditing(value: boolean) {
        this._state.ui.syncEditing = value;
    }

    get knownMarking(): boolean {
        return this._state.ui.knownMarking;
    }

    set knownMarking(value: boolean) {
        this._state.ui.knownMarking = value;
    }

    get maskMarking(): boolean {
        return this._state.ui.maskMarking;
    }

    set maskMarking(value: boolean) {
        this._state.ui.maskMarking = value;
    }

    get allowImages(): boolean {
        return this._state.ui.allowImages;
    }

    set allowImages(value: boolean) {
        this._state.ui.allowImages = value;
    }

    get hasGraphics(): boolean {
        return this._state.ui.hasGraphics;
    }

    set hasGraphics(value: boolean) {
        this._state.ui.hasGraphics = value;
    }

    get allowColored(): boolean {
        return this._state.ui.allowColored;
    }

    set allowColored(value: boolean) {
        this._state.ui.allowColored = value;
    }

    get allowBridge(): boolean {
        return this._state.ui.allowBridge;
    }

    set allowBridge(value: boolean) {
        this._state.ui.allowBridge = value;
    }

    get allowEmpty(): boolean {
        return this._state.ui.allowEmpty;
    }

    set allowEmpty(value: boolean) {
        this._state.ui.allowEmpty = value;
    }

    get allowBlack(): boolean {
        return this._state.ui.allowBlack;
    }

    set allowBlack(value: boolean) {
        this._state.ui.allowBlack = value;
    }

    get allowSolution(): boolean {
        return this._state.ui.allowSolution;
    }

    set allowSolution(value: boolean) {
        this._state.ui.allowSolution = value;
    }

    get hasMultipleGrids(): boolean {
        return this._state.ui.hasMultipleGrids;
    }

    set hasMultipleGrids(value: boolean) {
        this._state.ui.hasMultipleGrids = value;
    }

    // ============================================================================
    // Asset State
    // ============================================================================

    get imagePath(): string {
        return this._state.assets.imagePath;
    }

    set imagePath(value: string) {
        this._state.assets.imagePath = value;
    }

    get borderColor(): string {
        return this._state.assets.borderColor;
    }

    set borderColor(value: string) {
        this._state.assets.borderColor = value;
    }

    get coloredBase64(): string {
        return this._state.assets.coloredBase64;
    }

    set coloredBase64(value: string) {
        this._state.assets.coloredBase64 = value;
    }

    get bridgeBase64(): string {
        return this._state.assets.bridgeBase64;
    }

    set bridgeBase64(value: string) {
        this._state.assets.bridgeBase64 = value;
    }

    // ============================================================================
    // Validation State
    // ============================================================================

    get validCharPattern(): string {
        return this._state.validation.validCharPattern;
    }

    set validCharPattern(value: string) {
        this._state.validation.validCharPattern = value;
    }
}

/** Default application state instance */
export const appState = new AppState();
